import { KeycloakProfile } from "keycloak-js";
import queryString from "query-string";
import React, { useMemo, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { useTheme } from "styled-components";
import {
  AuthorizationVerifier,
  showSection,
  useRoleChecker,
} from "../../components/authorization-verifier";
import { IPropertyChainData, useChainData } from "../../components/chain-data-provider";
import { useLoyaltyBrand } from "../../components/loyalty-brand-provider";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { useWhiteLabel } from "../../components/white-label-provider";
import { useUserAuthInfo } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { SECTION, SVGSECTION } from "../../lib/constants";
import { usePermissions } from "../../lib/permissions";
import { CALL_CENTER_LIGHT_USER, CALL_CENTER_USER } from "../../lib/roles";
import { LINK_TYPES } from "../../modules/witadmin/Common/constants";
import { CallCenterLoyalty, MenuItem, MenuItemProps } from "./menu-elements";
import { NavMenu } from "./navMenu";

const messages = defineMessages({
  home: {
    id: "menu.home",
    defaultMessage: "Home",
  },
  manuals: {
    id: "menu.manuals",
    defaultMessage: "Manuals",
  },
  reservations: {
    id: "menu.reservations",
    defaultMessage: "Bookings",
  },
  reservationListing: {
    id: "menu.reservations.list",
    defaultMessage: "Listings",
  },
  reservationQuotation: {
    id: "menu.reservations.quotation",
    defaultMessage: "Cotización ",
  },
  reservationExpanded: {
    id: "menu.reservations.expandedList",
    defaultMessage: "Expanded List",
  },
  reservationInsert: {
    id: "menu.reservations.insert",
    defaultMessage: "Insert booking",
  },
  insertLoyal: {
    id: "menu.reservations.insertLoyal",
    defaultMessage: "Insert call center booking",
  },
  inventory: {
    id: "menu.inventory",
    defaultMessage: "Inventory",
  },
  inventoryManagement: {
    id: "menu.inventory.management",
    defaultMessage: "Management",
  },
  inventoryExtras: {
    id: "menu.inventory.extras",
    defaultMessage: "Extras / Services",
  },
  inventoryDefine: {
    id: "menu.inventory.define",
    defaultMessage: "Define Inventory",
  },
  inventoryPromotions: {
    id: "menu.inventory.promos",
    defaultMessage: "Promotions / Highlights",
  },
  inventoryDynamicPricing: {
    id: "menu.inventory.dynamicPricing",
    defaultMessage: "Dynamic pricing module",
  },
  integrations: {
    id: "menu.integrations",
    defaultMessage: "Parameterization",
  },
  integrationsGoogleAnalytics: {
    id: "menu.integrations.googleAnalytics",
    defaultMessage: "Google Analytics",
  },
  integrationsMarkupManager: {
    id: "menu.integrations.markupManager",
    defaultMessage: "Markup Manager",
  },
  integrationsCurrencies: {
    id: "menu.integrations.currency",
    defaultMessage: "Currencies",
  },
  integrationsLanguages: {
    id: "menu.integrations.languages",
    defaultMessage: "Languages",
  },
  integrationsOccupancyTypes: {
    id: "menu.integrations.occupancyTypes",
    defaultMessage: "Occupancy Types",
  },
  reports: {
    id: "menu.reports",
    defaultMessage: "Reports",
  },
  reportsBookingStatistics: {
    id: "menu.reports.bookingStatistics",
    defaultMessage: "Booking statistics",
  },
  reportsBookingsByConcept: {
    id: "menu.reports.bookingsByConcept",
    defaultMessage: "Bookings by concept",
  },
  reportsBookingsByState: {
    id: "menu.reports.bookingsByState",
    defaultMessage: "Bookings by state",
  },
  reportsStaysPerDay: {
    id: "menu.reports.staysPerDay",
    defaultMessage: "Stays per day",
  },
  reportsListOfQueries: {
    id: "menu.reports.listOfQueries",
    defaultMessage: "List of queries",
  },
  reportsBillingReport: {
    id: "menu.reports.billingReport",
    defaultMessage: "Billing report",
  },
  reportsWaitingListReport: {
    id: "menu.reports.waitingListReport",
    defaultMessage: "Waiting list report",
  },
  priceFeed: {
    id: "menu.reports.priceFeed",
    defaultMessage: "Price feed",
  },
  communications: {
    id: "menu.communications",
    defaultMessage: "Communications",
  },
  commWebMessages: {
    id: "menu.comm.webMessages",
    defaultMessage: "Web messages",
  },
  commAlerts: {
    id: "menu.comm.alerts",
    defaultMessage: "Alerts",
  },
  commEmailConfirmation: {
    id: "menu.comm.emailConfirmation",
    defaultMessage: "Email confirmation",
  },
  commEmailListing: {
    id: "menu.comm.emailListing",
    defaultMessage: "Listings for emailing",
  },
  commEmailPreStay: {
    id: "menu.comm.emailPreStay",
    defaultMessage: "Email pre-stay",
  },
  commEmailPostStay: {
    id: "menu.comm.emailPostStay",
    defaultMessage: "Email post-stay",
  },
  commPostStaySurvey: {
    id: "menu.comm.postStaySurvey",
    defaultMessage: "Post-stay survey",
  },
  commSurveyResults: {
    id: "menu.comm.surveyResults",
    defaultMessage: "Survey Results (by check-out date)",
  },
  commForms: {
    id: "menu.comm.forms",
    defaultMessage: "Forms",
  },
  email: {
    id: "menu.comm.email",
    defaultMessage: "Email",
  },
  commCustomEmails: {
    id: "menu.comm.customEmails",
    defaultMessage: "Custom emails",
  },
  settings: {
    id: "menu.settings",
    defaultMessage: "Settings",
  },
  settingsPropertyInfo: {
    id: "menu.settings.propertyInfo",
    defaultMessage: "Property Info",
  },
  settingsBehavior: {
    id: "menu.settings.behavior",
    defaultMessage: "Behavior",
  },
  settingsParameterization: {
    id: "menu.settings.parameterization",
    defaultMessage: "Parameterization",
  },
  settingsAppearance: {
    id: "menu.settings.appearance",
    defaultMessage: "Appearance",
  },
  settingsDisplay: {
    id: "menu.settings.display",
    defaultMessage: "Display",
  },
  settingsChangePassword: {
    id: "menu.settings.changePassword",
    defaultMessage: "Change Password",
  },
  settingsLegalInfo: {
    id: "menu.settings.legalInfo",
    defaultMessage: "Legal Info",
  },
  settingsModificationPolicies: {
    id: "menu.settings.modificationPolicies",
    defaultMessage: "Modification Policies",
  },
  settingsFilters: {
    id: "menu.settings.filters",
    defaultMessage: "Filters",
  },
  settingsCancellationPolicies: {
    id: "menu.settings.cancellationPolicies",
    defaultMessage: "Cancellation Policies",
  },
  loyalty: {
    id: "menu.loyalty",
    defaultMessage: "Clients",
  },
  loyaltyCustomers: {
    id: "menu.loyalty.customers",
    defaultMessage: "List of clients",
  },
  derivedLoyaltyTiers: {
    id: "menu.loyalty.derivedTiers",
    defaultMessage: "Loyalty levels",
  },
  constantLoyaltyTiers: {
    id: "menu.loyalty.constantTiers",
    defaultMessage: "Loyaly levels Companies-Agencies",
  },
  customerTransactionReport: {
    id: "menu.loyalty.customerTransactionReport",
    defaultMessage: "Customer transaction report",
  },
  loyaltyOrganizations: {
    id: "menu.loyalty.organizations",
    defaultMessage: "Companies-Agencies",
  },
  settingsTagsManager: {
    id: "menu.settings.tagsManager",
    defaultMessage: "Tags management",
  },
  customMapsManager: {
    id: "menu.settings.customMaps",
    defaultMessage: "Mapas personalizados",
  },
  customerInfoSettings: {
    id: "menu.settings.customerInfoSettings",
    defaultMessage: "Guest data",
  },
  feesManager: {
    id: "menu.settings.feesManager",
    defaultMessage: "Fees",
  },
  taxesManager: {
    id: "menu.settings.taxesManager",
    defaultMessage: "Taxes",
  },
  paymentMethods: {
    id: "menu.settings.paymentMethods",
    defaultMessage: "Payment methods",
  },
});

interface ClassicMenuProps {
  propertyTicker: string;
  useLegacyExtras?: boolean;
  userProfile?: KeycloakProfile & { attributes?: Record<string, any> } | undefined;
}

export const staticURL = "/v6/ng";

export function ClassicMenu({ propertyTicker, useLegacyExtras, userProfile }: ClassicMenuProps) {
  const intl = useIntl();
  const config = useConfig();
  const propertyConfig = usePropertyConfiguration();
  const whiteLabel = useWhiteLabel();
  const loyaltyBrand = useLoyaltyBrand();
  const history = useHistory();
  const chainData: IPropertyChainData | undefined | null = useChainData();
  const theme = useTheme();
  const userAuthInfo = useUserAuthInfo();

  const isCallCenterUser = useRoleChecker([CALL_CENTER_LIGHT_USER, CALL_CENTER_USER]);
  const showEmailTemplateManager = useMemo(
    () => propertyConfig && propertyConfig?.["showEmailTemplateManager"] === "1",
    [propertyConfig]
  );
  const showAdvancedEmailsManager = useMemo(
    () => propertyConfig && propertyConfig?.["showAdvancedEmailsManager"] === "1",
    [propertyConfig]
  );
  const showNewReservationsList = useMemo(
    () => propertyConfig && propertyConfig?.["showNewReservationsList"] === "1",
    [propertyConfig]
  );
  const showTagManagement = useMemo(() => chainData?.isChain, [chainData]);

  const urlKnowledgeDatabase = useMemo(() => {
    const link = whiteLabel?.externalSiteConfigurations.find(
      link => link.externalSiteType === LINK_TYPES.KNOWLEDGE_DATABASE
    );
    return link?.url;
  }, [whiteLabel]);

  const userAttributes = useMemo(() => {
    let attributes = {};
    if (userProfile && userProfile.attributes) {
      attributes = Object.entries(userProfile.attributes)
        .map(([key, value]) => [key, Array.isArray(value) && value.length > 0 ? value[0] : ""])
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {} as { [key: string]: string });
    }
    return JSON.stringify(attributes);
  }, [userProfile]);

  const refMenu: any = useRef(null);

  const isSelenta =
    propertyTicker === "sofia" ||
    propertyTicker === "expovalencia" ||
    propertyTicker === "expobarcelona" ||
    propertyTicker === "cleopatrapalace" ||
    propertyTicker === "mediterraneanpalace" ||
    propertyTicker === "hoteldoncarlos" ||
    propertyTicker === "siranthonyhotel" ||
    propertyTicker === "theoasis";

  const insertReservationUrl = queryString.stringifyUrl({
    url: `${config.engineBaseUrl}/${chainData?.isChain ? "chain" : "hotel"}/${propertyTicker}`,
    query: {
      encodedAffiliate: userProfile ? btoa(userProfile.email || "") : undefined,
      origin: isCallCenterUser ? "callcenter" : "backoffice",
      disableAbTest: "1",
      encodedAffiliateAttributes: btoa(userAttributes),
    },
  });

  const hideDashboardOption = useMemo(() => chainData?.isChain, [chainData]);
  const permissions = usePermissions();
  const menu = useMemo(
    () =>
      [
        {
          title: intl.formatMessage(messages.reservations),
          section: SECTION.MAIN_RESEREVATIONS,
          mainSection: SECTION.MAIN_RESEREVATIONS,
          routes: ["/reservations"],
          menuItems: [
            {
              message: messages.reservationListing,
              route: `/${propertyTicker}/reservations`,
              section: SECTION.RESERVATIONS_LIST,
              icon: SVGSECTION.reservationListing,
            },
            {
              message: messages.reservationExpanded,
              route: `/${propertyTicker}/reservationsMulti`,
              section: SECTION.RESERVATIONS_LIST_EXTENDED,
              icon: SVGSECTION.reservationExpanded,
            },
            {
              message: messages.reservationInsert,
              href: insertReservationUrl,
              section: SECTION.RESERVATIONS_LIST,
              target: "_blank",
              icon: SVGSECTION.reservationInsert,
            },
            {
              hideItem: !loyaltyBrand,
              children: (
                <CallCenterLoyalty
                  message={messages.insertLoyal}
                  propertyTicker={propertyTicker}
                  userProfile={userProfile}
                  engineBaseUrl={config.engineBaseUrl}
                  loyaltyBrand={loyaltyBrand}
                  icon={SVGSECTION.reservationCallCenter}
                />
              ),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.inventory),
          section: SECTION.MAIN_INVENTORIES,
          mainSection: SECTION.MAIN_INVENTORIES,
          routes: [
            "/ari-management",
            "/extras",
            "/define-inventory",
            "/promotions",
            "/accommodations",
            "/batch-inventory",
            "/inventory-table",
            "/conditions",
            "/mealplan",
            "/inventoryMultiUpdate",
          ],
          menuItems: [
            {
              message: messages.inventoryManagement,
              route: `/${propertyTicker}/ari-management`,
              section: SECTION.INVENTORY_MANAGEMENT,
              icon: SVGSECTION.inventoryManagement,
            },
            {
              message: messages.inventoryExtras,
              route: `/${propertyTicker}/extras`,
              section: SECTION.EXTRAS,
              icon: SVGSECTION.inventoryExtras,
            },
            {
              message: messages.inventoryDefine,
              route: `/${propertyTicker}/define-inventory`,
              section: SECTION.INVENTORY_CONFIGURATION,
              icon: SVGSECTION.inventoryDefine,
            },
            {
              message: messages.inventoryPromotions,
              route: `/${propertyTicker}/promotions`,
              section: SECTION.PROMOTIONS,
              icon: SVGSECTION.inventoryPromotions,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.integrations),
          section: SECTION.MAIN_INTEGRATIONS,
          mainSection: SECTION.MAIN_INTEGRATIONS,
          menuItems: [
            {
              message: messages.integrationsGoogleAnalytics,
              route: `/${propertyTicker}/integrations/analytics-management`,
              section: SECTION.GOOGLE_ANALYTICS,
              icon: SVGSECTION.integrationsGoogleAnalytics,
            },
            {
              message: messages.integrationsLanguages,
              route: `/${propertyTicker}/languages`,
              section: SECTION.LANGUAGE_SETTINGS,
              icon: SVGSECTION.integrationsLanguages,
            },
            {
              message: messages.integrationsCurrencies,
              route: `/${propertyTicker}/currencies`,
              section: SECTION.CURRENCY_SETTINGS,
              icon: SVGSECTION.integrationsCurrencies,
            },
            {
              message: messages.customerInfoSettings,
              route: `/${propertyTicker}/engine-guest-data`,
              section: SECTION.CUSTOMER_INFO_SETTINGS,
              icon: SVGSECTION.customerInfoSettings,
            },
            {
              message: messages.feesManager,
              route: `/${propertyTicker}/fees`,
              section: SECTION.FEES_MANAGER,
              icon: SVGSECTION.feesManager,
            },
            {
              message: messages.taxesManager,
              route: `/${propertyTicker}/taxes`,
              section: SECTION.TAXES_MANAGER,
              icon: SVGSECTION.taxesManager,
            },
            {
              message: messages.paymentMethods,
              route: `/${propertyTicker}/paymentTypes`,
              section: SECTION.PAYMENT_METHODS,
              icon: SVGSECTION.paymentMethods,
            },

            {
              message: messages.integrationsOccupancyTypes,
              route: `/${propertyTicker}/occupancy-types`,
              section: SECTION.OCCUPANT_SETTINGS,
              icon: SVGSECTION.integrationsOccupancyTypes,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.reports),
          section: SECTION.MAIN_REPORTS,
          mainSection: SECTION.MAIN_REPORTS,
          routes: ["/priceFeed"],
          menuItems: [
            {
              message: messages.reportsBookingStatistics,
              route: `/${propertyTicker}/report`,
              section: SECTION.RESERVATION_STATS,
              icon: SVGSECTION.reportsBookingStatistics,
            },
            {
              message: messages.reportsBookingsByConcept,
              route: `/${propertyTicker}/report-concept`,
              section: SECTION.RESERVATION_STATS,
              icon: SVGSECTION.reportsBookingsByConcept,
            },
            {
              message: messages.reportsBookingsByState,
              route: `/${propertyTicker}/report-status`,
              section: SECTION.RESERVATION_STATS,
              icon: SVGSECTION.reportsBookingsByState,
            },
            {
              message: messages.reportsStaysPerDay,
              route: `/${propertyTicker}/report-stays-per-day`,
              section: SECTION.STAYS_PER_DAY,
              icon: SVGSECTION.reportsStaysPerDay,
            },
            {
              message: messages.reportsListOfQueries,
              route: `/${propertyTicker}/report-queries`,
              section: SECTION.QUERY_STATS,
              icon: SVGSECTION.reportsListOfQueries,
            },
            {
              message: messages.reportsBillingReport,
              route: `/${propertyTicker}/billing-report`,
              section: SECTION.BILLING_REPORT,
              icon: SVGSECTION.reportsBillingReport,
            },
            {
              message: messages.reportsWaitingListReport,
              route: `/${propertyTicker}/waiting-list-report`,
              section: SECTION.WAITING_LIST_REPORT,
              icon: SVGSECTION.reportsBillingReport,
            },
            {
              message: messages.priceFeed,
              route: `/${propertyTicker}/priceFeed`,
              section: SECTION.PRICE_FEED,
              icon: SVGSECTION.reportsPriceFeed,
              hideItem: !isSelenta,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.communications),
          section: SECTION.MAIN_COMMUNICATIONS,
          mainSection: SECTION.MAIN_COMMUNICATIONS,
          routes: ["/questionnaire", "emailTemplate"],
          menuItems: [
            {
              message: messages.commWebMessages,
              route: `/${propertyTicker}/web-message`,
              section: SECTION.WEB_MESSAGES,
              icon: SVGSECTION.commWebMessages,
            },
            {
              message: messages.commAlerts,
              route: `/${propertyTicker}/communication-alerts`,
              section: SECTION.ALERT_SETTINGS,
              icon: SVGSECTION.commAlerts,
            },
            {
              message: messages.commForms,
              route: `/${propertyTicker}/questionnaire`,
              section: SECTION.QUESTIONNAIRES,
              icon: SVGSECTION.commForms,
            },
            {
              message: messages.commCustomEmails,
              route: `/${propertyTicker}/emailTemplate`,
              section: SECTION.CUSTOM_EMAIL,
              icon: SVGSECTION.commCustomEmails,
              hideItem: !(showEmailTemplateManager || showAdvancedEmailsManager),
            },
          ],
        },
        {
          title: intl.formatMessage(messages.settings),
          section: SECTION.MAIN_SETTINGS,
          mainSection: SECTION.MAIN_SETTINGS,
          routes: [
            "/display",
            "/legalInfo",
            "/modification",
            "/filter",
            "/cancellationPolicy",
            "/tags",
            "/customMapsManager",
          ],
          menuItems: [
            {
              message: messages.settingsPropertyInfo,
              route: `/${propertyTicker}/property-info`,
              section: SECTION.PROPERTY_INFO,
              icon: SVGSECTION.commWebMessages,
            },
            {
              message: messages.settingsBehavior,
              route: `/${propertyTicker}/behaviour`,
              section: SECTION.BEHAVIOR_SETTINGS,
              icon: SVGSECTION.settingsBehavior,
            },
            {
              message: messages.settingsAppearance,
              route: `/${propertyTicker}/engine-appearance`,
              section: SECTION.APPEARANCE_SETTINGS,
              icon: SVGSECTION.settingsAppearance,
            },
            {
              message: messages.settingsDisplay,
              route: `/${propertyTicker}/display`,
              section: SECTION.DISPLAY,
              icon: SVGSECTION.settingsDisplay,
            },
            {
              message: messages.settingsLegalInfo,
              route: `/${propertyTicker}/legalInfo`,
              section: SECTION.LEGAL_INFO,
              icon: SVGSECTION.settingsLegalInfo,
            },
            {
              message: messages.settingsModificationPolicies,
              route: `/${propertyTicker}/modification`,
              section: SECTION.RESERVATION_MODIFICATION_POLICY,
              icon: SVGSECTION.settingsModificationPolicies,
            },
            {
              message: messages.settingsFilters,
              route: `/${propertyTicker}/filter`,
              section: SECTION.FILTERS,
              icon: SVGSECTION.settingsFilters,
            },
            {
              message: messages.settingsCancellationPolicies,
              route: `/${propertyTicker}/cancellationPolicy`,
              section: SECTION.CANCELLATION_POLICY,
              icon: SVGSECTION.settingsCancellationPolicies,
            },
            {
              message: messages.settingsTagsManager,
              route: `/${propertyTicker}/tags`,
              section: SECTION.TAGS_MANAGER,
              icon: SVGSECTION.tagsManagement,
              hideItem: showTagManagement,
            },
            {
              message: messages.customMapsManager,
              route: `/${propertyTicker}/customMapsManager`,
              section: SECTION.MAPS_MANAGER,
              icon: SVGSECTION.mapsManagement,
              hideItem: !userAuthInfo?.isWitAdmin,
            },
          ],
        },
        {
          title: intl.formatMessage(messages.loyalty),
          section: SECTION.MAIN_LOYALTY,
          mainSection: SECTION.MAIN_LOYALTY,
          hideMenu: !loyaltyBrand?.ticker,
          routes: [
            "/loyalty-customers",
            "/loyalty-tiers-derived",
            "/loyalty-tiers-constant",
            "/loyalty-customer-transaction-report",
            "/loyalty-organizations",
          ],
          menuItems: [
            {
              message: messages.loyaltyCustomers,
              route: `/${propertyTicker}/loyalty-customers`,
              section: SECTION.LOYALTY_CUSTOMERS,
              icon: SVGSECTION.loyaltyCustomers,
            },
            {
              message: messages.derivedLoyaltyTiers,
              route: `/${propertyTicker}/loyalty-tiers-derived`,
              section: SECTION.LOYALTY_DERIVED_TIERS,
              icon: SVGSECTION.derivedLoyaltyTiers,
            },
            {
              message: messages.constantLoyaltyTiers,
              route: `/${propertyTicker}/loyalty-tiers-constant`,
              section: SECTION.LOYALTY_CONSTANT_TIERS,
              icon: SVGSECTION.constantLoyaltyTiers,
            },
            {
              message: messages.customerTransactionReport,
              route: `/${propertyTicker}/loyalty-customer-transaction-report`,
              section: SECTION.LOYALTY_CUSTOMER_TRANSACTION_REPORT,
              icon: SVGSECTION.customerTransactionReport,
            },
            {
              message: messages.loyaltyOrganizations,
              route: `/${propertyTicker}/loyalty-organizations`,
              section: SECTION.LOYALTY_ORGANIZATIONS,
              icon: SVGSECTION.loyaltyOrganizations,
            },
          ],
        },
      ]
        .map(it => ({
          ...it,
          menuItems: (it.menuItems as MenuItemProps[]).filter(mi => {
            const showForced = mi.hideItem === undefined ? true : !Boolean(mi.hideItem);
            if (mi.section === undefined) {
              return showForced;
            }
            const hasPermissions = showSection(permissions, mi.section);
            return hasPermissions && showForced;
          }),
        }))
        .filter(m => m.menuItems.length > 0),
    [
      intl,
      propertyTicker,
      insertReservationUrl,
      loyaltyBrand,
      userProfile,
      config.engineBaseUrl,
      isSelenta,
      showEmailTemplateManager,
      showAdvancedEmailsManager,
      showTagManagement,
      userAuthInfo?.isWitAdmin,
      permissions,
    ]
  );
  return (
    <div className="flex h-5" ref={refMenu}>
      <div
        className={`flex gap-[20px] ${
          userAuthInfo?.isWitAdmin && `border-[${theme.textColor}] border-b-[0.5px] pb-3 gap-[20px]`
        }  `}>
        {!hideDashboardOption && (
          <AuthorizationVerifier section={SECTION.DASHBOARD} showForbiddenMessage={false}>
            <NavMenu
              title={intl.formatMessage(messages.home)}
              section={SECTION.DASHBOARD}
              handleClick={() => history.push(`/${propertyTicker}/dashboard`)}
              routes={["/dashboard"]}
            />
          </AuthorizationVerifier>
        )}
        {menu
          .filter(({ hideMenu }) => !hideMenu)
          .map(({ title, section, mainSection, menuItems, routes }, navIndex) => (
            <AuthorizationVerifier
              key={navIndex}
              section={mainSection}
              showForbiddenMessage={false}>
              <NavMenu title={title} section={section} routes={routes}>
                {menuItems && (
                  <ul className="p-[20px] text-sm mb-0" aria-labelledby="dropdownNavbarButton">
                    {menuItems &&
                      (menuItems as any[]).reduce(
                        (acc: any[], item: MenuItemProps, index: number): any => {
                          const {
                            children,
                            hideItem,
                            message,
                            href,
                            icon,
                            target,
                            route,
                            section: sectionChild,
                          } = item;

                          if (!hideItem) {
                            if (children) {
                              return [...acc, <li key={index}>{children}</li>];
                            }
                            acc = [
                              ...acc,
                              <li key={index}>
                                <MenuItem
                                  message={message}
                                  href={href}
                                  section={sectionChild}
                                  icon={icon}
                                  target={target}
                                  route={route}
                                />
                              </li>,
                            ];
                          }
                          return acc;
                        },
                        []
                      )}
                  </ul>
                )}
              </NavMenu>
            </AuthorizationVerifier>
          ))}
        <div
          className="border-none no-underline flex items-center justify-between cursor-pointer"
          onClick={() => window.open(urlKnowledgeDatabase, "_blank")}>
          {intl.formatMessage(messages.manuals)}
        </div>
      </div>
    </div>
  );
}
